/* eslint-disable react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Chip,
  FormHelperText,
  IconButton,
  TextField,
  Tooltip
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';

/**
 * Multi-email input rendered as chips — type an address and press Enter.
 * Mirrors the "Secondary Email" field on the payment request form.
 * `errors` is the per-index error array Formik produces for an array field.
 */
function EmailChipsField({
  name,
  label = 'Secondary Email',
  placeholder = 'Enter Email',
  value = [],
  errors = [],
  disabled = false,
  onChange,
  helpText = ''
}) {
  const emails = Array.isArray(value) ? value : [];
  const errorList = Array.isArray(errors) ? errors : [];
  const hasError = errorList.some((item) => !!item);

  return (
    <Box sx={{ position: 'relative' }}>
      {!disabled && helpText ? (
        <Tooltip title={helpText} arrow placement="bottom-start">
          <IconButton
            sx={{ position: 'absolute', top: '-11px', right: 0, zIndex: 1 }}
            size="small"
            aria-label="info"
          >
            <HelpIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      ) : null}
      <Autocomplete
        options={[]}
        multiple
        freeSolo
        limitTags={2}
        fullWidth
        disabled={disabled}
        value={emails}
        onChange={(e, newValue) => {
          const cleaned = newValue
            .map((item) => (typeof item === 'string' ? item.replace(/\s+/gi, '').trim() : item))
            .filter((item) => item !== '');
          onChange(cleaned);
        }}
        getOptionLabel={(option) => option}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            const invalidEmail = errorList[index];
            return (
              <div className="tagsValue" key={index}>
                <Tooltip title={invalidEmail || option} arrow>
                  <Chip
                    size="small"
                    color={invalidEmail ? 'error' : 'secondary'}
                    variant="outlined"
                    sx={{ maxWidth: '90px !important' }}
                    label={option}
                    {...tagProps}
                    onDelete={
                      disabled ? undefined : () => onChange(emails.filter((_, i) => i !== index))
                    }
                  />
                </Tooltip>
                {invalidEmail && <FormHelperText error>{invalidEmail}</FormHelperText>}
              </div>
            );
          })
        }
        renderOption={(props, option) => (
          <li {...props} key={option}>
            {option}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            error={hasError}
            label={label}
            placeholder={placeholder}
            variant="outlined"
            margin="normal"
          />
        )}
      />
    </Box>
  );
}

// Defaults are declared as parameter defaults above — `defaultProps` on a
// function component is deprecated in React 18 and logs a warning.
/* eslint-disable react/require-default-props */
EmailChipsField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  errors: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  helpText: PropTypes.string
};

export default EmailChipsField;
